import React, { useState, useEffect } from 'react';
import { SaveParticipant } from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';
import PersonalInfoCard from './PersonalInfoCard';
import { BreederInfoInput } from './breeder-sections';
import HandlerInfoCard from './HandlerInfoCard';

export default function PersonalInfoTab({ 
  participant, 
  onMessage, 
  onParticipantUpdate, 
  onTabSwitch,
  isEditing,
  onEditingChange,
  saving,
  setSaving,
  onSaveHandlerReady 
}) {
  const [editedData, setEditedData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (participant) {
      setEditedData(participant);
    }
  }, [participant]);

  useEffect(() => {
    // When exiting edit mode, ensure we have the latest data
    if (!isEditing && participant) {
      setEditedData(participant);
    }
  }, [isEditing, participant]);

  // Register save handler with parent component (global save controls)
  useEffect(() => {
    if (isEditing) {
      window.personalInfoTabSave = handleSave;
    }
    return () => {
      delete window.personalInfoTabSave;
    };
  }, [isEditing, editedData]);
  const validateNotificationPreferences = () => {
    const errors = {};
    const currentEmail = editedData.emailAddress || participant.emailAddress;
    const currentMobile = editedData.mobile || participant.mobile;
    const notificationMethods = editedData.preferredNotificationMethod || [];
    
    // Validate email format if provided
    if (editedData.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedData.emailAddress)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Validate mobile format if provided (basic US phone format)
    if (editedData.mobile && !/^[\d\s\-\(\)\+\.]{10,}$/.test(editedData.mobile.replace(/\D/g, '')) && editedData.mobile.replace(/\D/g, '').length < 10) {
      errors.mobile = 'Please enter a valid mobile number (at least 10 digits)';
    }
    
    // Validate notification preferences match available contact info
    if (notificationMethods.includes('email') && !currentEmail?.trim()) {
      errors.notification = 'Email address is required for email notifications';
    }
    
    if (notificationMethods.includes('text') && !currentMobile?.trim()) {
      errors.notification = errors.notification 
        ? 'Email address and mobile number are required for selected notification methods'
        : 'Mobile number is required for text notifications';
    }
    
    return errors;
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation errors when user makes changes
    if (validationErrors.notification) {
      setValidationErrors({});
    }
  };

  const handleEdit = () => {
    // Ensure editedData has all current participant data including roles
    setEditedData(participant);
    if (onEditingChange) {
      onEditingChange(true);
    }
  };

  const handleCancel = () => {
    if (onEditingChange) {
      onEditingChange(false);
    }
    setEditedData(participant);
    if (onMessage) {
      onMessage('');
    }
  };

  const handleSave = async () => {
    // Validate notification preferences first - don't set saving state if validation fails
    const errors = validateNotificationPreferences();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      if (onMessage) {
        const errorMessage = errors.email || errors.mobile || errors.notification;
        onMessage(errorMessage);
      }
      return; // Exit early, stay in edit mode, no loading state
    }
    
    // Only set saving state after validation passes
    if (setSaving) {
      setSaving(true);
    }
    
    try {
      const authData = getLoggedInParticipant();
      const result = await SaveParticipant(editedData, authData?.authToken);
      
      // API returns participant object directly, not wrapped in success object
      if (result && result.id) {
        
        // Show loading message while reloading data
        if (onMessage) {
          onMessage('Loading updated information...');
        }
        
        // Trigger participant data update
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        // Show success message after data is loaded
        if (onMessage) {
          onMessage('Your information has been updated successfully!');
        }
      } else {
        if (onMessage) {
          onMessage(result?.message || 'Failed to update information. Please try again.');
        }
        // Go back to edit mode if save failed
        if (onEditingChange) {
          onEditingChange(true);
        }
      }
    } catch (error) {
      if (onMessage) {
        onMessage('An error occurred. Please try again.');
      }
      // Go back to edit mode if error occurred
      if (onEditingChange) {
        onEditingChange(true);
      }
    } finally {
      if (setSaving) {
        setSaving(false);
      }
    }
  };

  if (!participant) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading personal information...</div>
      </div>
    );
  }

  return (
    <>
      <PersonalInfoCard
          fullName={isEditing ? editedData.fullName : participant.fullName}
          loftName={isEditing ? editedData.loftName : participant.loftName}
          emailAddress={isEditing ? (editedData.emailAddress || participant.emailAddress) : participant.emailAddress}
          mobile={isEditing ? (editedData.mobile || participant.mobile) : participant.mobile}
          roles={Array.isArray(participant.role) ? participant.role : [participant.role]}
          notificationMethods={isEditing ? (editedData.preferredNotificationMethod || []) : (participant.preferredNotificationMethod || [])}
          hideContactInfo={isEditing ? editedData.hideContactInfo : participant.hideContactInfo}
          editable={isEditing}
          onEdit={true}
          onChange={handleInputChange}
          editedData={editedData}
          validationErrors={validationErrors}
          originalEmailAddress={participant.emailAddress || ''}
          originalMobile={participant.mobile || ''}
        />
      
      <BreederInfoInput
      totalBirdsForRace={editedData?.breederInfo?.totalBirdsForRace}
      editedData={editedData}
        participant={participant}
        onMessage={onMessage}
        onChange={handleInputChange}
        onParticipantUpdate={onParticipantUpdate}
        isEditing={true}
        onEditingChange={onEditingChange}
        saving={saving}
        setSaving={setSaving}
      />
      
      <HandlerInfoCard
        participant={participant}
        onMessage={onMessage}
        onParticipantUpdate={onParticipantUpdate}
        isEditing={true}
        onEditingChange={onEditingChange}
        saving={saving}
        setSaving={setSaving}
        onChange={handleInputChange}
        editedData={editedData}
      />
    </>
  );
}